import { useUsers } from 'data/user';
import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';

import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import SendRoundedIcon from '@material-ui/icons/SendRounded';

import Button from 'creative-components/CustomButtons/Button';
import ReactTable from 'creative-components/ReactTable/ReactTable';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import UserInfoModal from 'components/UsersTable/UserInfoModal';

import { authenticateAssumeUser, resendSignUpEmail } from 'utils/api';
import { enumToReadableName, showAPIErrorAlert } from 'utils/lib';

const styles = {

};

const useStyles = makeStyles(styles);

export default function UsersTable() {
  const classes = useStyles();
  const { onLogin } = useAuthDataContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();

  const { users, isLoadingUsers } = useUsers();

  const [infoModal, setInfoModal] = React.useState(null);

  const [data, setData] = React.useState([]);

  const onAssumeUser = async (userId) => {
    showLoadingIndicatorModal();
    try {
      const { token, expiresInDays } = await authenticateAssumeUser(userId);

      // Call the login function on the auth context
      onLogin(token, expiresInDays);
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    }

    hideLoadingIndicatorModal();
  };

  const onResendSignUpEmail = async (userId, firstName, lastName) => {
    showLoadingIndicatorModal();
    try {
      await resendSignUpEmail(userId);

      toast.success(`Re-sent a sign up email to ${firstName} ${lastName}`);
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    }

    hideLoadingIndicatorModal();
  };

  React.useEffect(() => {
    if (isLoadingUsers) return;

    setData(users.map((user) => ({
      id: user._id,
      name: `${user.firstName} ${user.lastName ?? ''}`,
      email: user.email,
      role: user.role.name,
      status: enumToReadableName(user.status),
      createdAt: moment(user.createdAt).toDate(),
      actions: (
        <div>
          {user.status === 'ACTIVE' && (
            <Button
              onClick={() => onAssumeUser(user._id)}
              color="orange"
              round
              size="sm"
            >
              Impersonate
            </Button>
          )}
          {user.status === 'UNREGISTERED' && (
            <Tooltip
              title="Resend Invite Email"
              placement="top"
              classes={{ tooltip: classes.tooltip }}
            >
              <Button
                justIcon
                round
                simple
                onClick={() => onResendSignUpEmail(user._id, user.firstName, user.lastName)}
                color="primary"
              >
                <SendRoundedIcon />
              </Button>
            </Tooltip>
          )}
          <Tooltip
            title="Info"
            placement="top"
            classes={{ tooltip: classes.tooltip }}
          >
            <Button
              justIcon
              round
              simple
              onClick={() => {
                setInfoModal(() => (
                  <UserInfoModal
                    user={user}
                    onClose={() => { setInfoModal(null); }}
                  />
                ));
              }}
              color="dark"
            >
              <InfoIcon />
            </Button>
          </Tooltip>
        </div>
      ),
    })));
  }, [isLoadingUsers, users]);

  if (isLoadingUsers) return <LoadingIndicator />;

  return (
    <>
      {infoModal}

      <ReactTable
        columns={[
          {
            Header: 'Name',
            accessor: 'name',
          },
          {
            Header: 'Email',
            accessor: 'email',
          },
          {
            Header: 'ID',
            accessor: 'id',
          },
          {
            Header: 'Role',
            accessor: 'role',
          },
          {
            Header: 'Status',
            accessor: 'status',
          },
          {
            Header: 'Date Created',
            accessor: 'createdAt',
            Cell: ({ cell: { value } }) => moment(value).format('MMM D, YYYY'),
            sortType: 'datetime',
          },
          {
            Header: 'Actions',
            accessor: 'actions',
          },
        ]}
        data={data}
      />
    </>
  );
}
